import moment from 'moment';
import { jwtDecode } from 'jwt-decode';

import { AsyncStorage } from '../../services/AsyncStorage';
import { Sentry } from '../../services/sentry';
import { config } from '../../config';
import { getAccessToken, isOidcSession } from '../../services/tokens';
import { refreshOidcSession } from '../../services/auth/session';
import {
  DPOP_NONCE_SCOPE,
  buildAuthorizationHeaders,
  isAccessTokenUsableWithDpopKey,
  isDpopNonceRequired,
  isDpopProofRejected,
  rememberDpopNonce,
  reportDpopNonceChallenge,
  reportDpopProofRejected,
} from '../../services/auth/dpop';
import {
  LANGUAGE_TO_LANGUAGE_HEADER,
  COUNTRY_CODE_TO_LANGUAGE_HEADER,
} from '../../services';

import { AuthError, ApiError } from '../errors';
import { normalizeEndpoint } from './apiErrorContext';

let impersonateId = null;

/**
 * Create a User-Agent string for this client
 *
 * User-Agent: Name/version (Mobile; Platform; bundleId; countryCode)
 *
 * Native client:
 * User-Agent: FormueApp/2020.04.2312 (Mobile; iOS; no.formue.formue.no; no)
 *
 * Web client:
 * User-Agent: FormueApp/2020.04.2312 (Web; iOS; no.formue.formue.no; no)
 */
export const getUserAgent = (options) => {
  const name = 'FormueApp';
  const {
    app: { bundleId, appVersion, platform, devicePlatform },
    countryCode: country,
  } = config;

  let countryCode = country;
  // Use country code from options if it is provided
  if (options && options.country) {
    countryCode = options.country;
  }

  return `${name}/${appVersion} (${platform}; ${devicePlatform}; ${bundleId}; ${countryCode})`;
};

/**
 * Create accept language string, we send the user selected locale
 * as the highest priority one, but fallbacks to default country code
 * and wildcard as the lowest priority one.
 *
 * https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Accept-Language
 * https://developer.mozilla.org/en-US/docs/Glossary/quality_values
 *
 * Eg. en;q=1, no;q=0.8 *;q=0.5
 */
const getAcceptLanguage = async () => {
  const languages = [
    `${COUNTRY_CODE_TO_LANGUAGE_HEADER[config.countryCode]};q=0.8`,
    '*;q=0.5',
  ];

  try {
    const selectedLanguage = await AsyncStorage.getItem('language');
    if (selectedLanguage) {
      const headerValue = `${LANGUAGE_TO_LANGUAGE_HEADER[selectedLanguage]};q=1`;
      headerValue && languages.unshift(headerValue);
    }
  } catch (e) {
    // we don't really care if this fails, we fall back to the other languages
  }

  return languages.join(' ');
};

/**
 * `retried` records the one-shot retries this request has already used —
 * `afterRefresh` (an /oidc session refreshed after a 401) and `withNonce` (a
 * DPoP nonce challenge answered) — so each happens at most once per request
 * and the two together bound a request at three attempts.
 */
const fetchApiResponse = async (
  relativeUrl,
  options,
  headers = {},
  impersonate = true,
  retried = {}
) => {
  const {
    app: { bundleId },
    api: { host },
  } = config;

  // Kept untouched so a retry after an /oidc refresh sends the same request.
  const requestHeaders = headers;

  headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json; charset=utf-8',
    'User-Agent': getUserAgent(options),
    'X-User-Agent': getUserAgent(options),
    'X-App-Platform': config.app.platform,
    'X-App-Identifier': bundleId,
    ...headers,
  };

  const normalizedRelativeUrl =
    relativeUrl.substring(0, 1) === '/'
      ? relativeUrl.substring(1)
      : relativeUrl;

  const baseUrl = options.baseUrl || config.api.baseUrl;
  const normalizedBaseUrl =
    baseUrl.substring(-1) === '/' ? baseUrl : `${baseUrl}/`;

  const url = `${host}${normalizedBaseUrl}${normalizedRelativeUrl}`;
  let accessToken = await getAccessToken();

  // Check if access token is about to expire, or is bound to a DPoP key we no
  // longer hold, if it is, then re-authenticate the user
  if (accessToken) {
    const parsedToken = jwtDecode(accessToken);
    const now = moment().format('X');
    if (
      parsedToken.exp - 60 <= now ||
      !(await isAccessTokenUsableWithDpopKey(accessToken))
    ) {
      // A session issued by the /oidc provider has a rotating refresh token
      // and short-lived (15 min) access tokens: refresh in place instead of
      // refusing. Parallel callers share the one in-flight refresh. Legacy
      // (/auth) sessions keep today's behaviour — they are never key-bound, so
      // the usability check only ever fires for an /oidc session.
      if (await isOidcSession()) {
        ({ accessToken } = await refreshOidcSession());
      } else {
        throw new AuthError('Token is about to expire, refusing to use it');
      }
    }
  }

  let body;
  if (options.body) {
    body =
      typeof options.body === 'string'
        ? options.body
        : JSON.stringify(options.body);
  }

  const method = (options.method || 'GET').toUpperCase();

  // Bearer for an unbound token, DPoP + proof for a key-bound one.
  const authHeaders = await buildAuthorizationHeaders({
    accessToken,
    method,
    url,
  });
  Object.assign(headers, authHeaders);

  headers['Accept-Language'] = await getAcceptLanguage();

  if (impersonateId && impersonate) {
    headers['x-impersonate-id'] = impersonateId;
  }

  const endpoint = normalizeEndpoint(relativeUrl);
  const startedAt = Date.now();

  let response;
  try {
    response = await fetch(url, {
      ...options,
      headers,
      body,
    });
  } catch (error) {
    // `fetch` itself rejected (e.g. `TypeError: Network request failed`, DNS
    // failure, abort). These errors are otherwise anonymous in Sentry — no URL,
    // no app frames — so attach the endpoint/timing before rethrowing.
    error.location = relativeUrl;
    error.endpoint = endpoint;
    error.method = method;
    error.durationMs = Date.now() - startedAt;
    error.isNetworkError = true;

    Sentry.addBreadcrumb({
      category: 'api',
      level: 'info',
      data: {
        endpoint,
        method,
        status: 'network',
        durationMs: error.durationMs,
      },
    });

    throw error;
  }

  const durationMs = Date.now() - startedAt;

  // Lean breadcrumb on every request (no full Response object / PII), so a later
  // captured error carries a recent request/timing trail.
  Sentry.addBreadcrumb({
    category: 'api',
    level: 'info',
    data: {
      endpoint,
      method,
      status: response.status,
      durationMs,
    },
  });

  // RFC 9449 §9: the API may want a nonce in the proof. It names the one it
  // wants in `DPoP-Nonce` — on any response, so it is kept for the next proof
  // — and rejects a proof without it as a 401 `use_dpop_nonce`. That 401 is
  // answered once with a fresh proof carrying the nonce; it says nothing
  // about the token, so it is never a reason to refresh, and a second one
  // falls through to the ordinary error below.
  const nonce = rememberDpopNonce(DPOP_NONCE_SCOPE.API, response.headers);
  const nonceChallenge =
    Boolean(authHeaders.DPoP) && isDpopNonceRequired(response);

  if (nonceChallenge && nonce && !retried.withNonce) {
    reportDpopNonceChallenge({ endpointKind: 'api', htu: url });
    return fetchApiResponse(relativeUrl, options, requestHeaders, impersonate, {
      ...retried,
      withNonce: true,
    });
  }

  // An /oidc access token the API no longer accepts (revoked, or expired by
  // clock skew) is refreshed once and the request retried once. If the refresh
  // fails the original 401 is thrown below and the app's 401 handling takes
  // over; the stored session has been cleared by then if the refresh token
  // was rejected.
  // A 401 that names our proof as the problem is the clock-skew signal — see
  // reportDpopProofRejected. The refresh-and-retry below runs as usual.
  if (isDpopProofRejected(response)) {
    reportDpopProofRejected({
      endpointKind: 'api',
      htu: url,
      proof: authHeaders.DPoP,
    });
  }

  if (
    response.status === 401 &&
    accessToken &&
    !retried.afterRefresh &&
    !nonceChallenge &&
    (await isOidcSession())
  ) {
    let refreshed = false;
    try {
      await refreshOidcSession();
      refreshed = true;
    } catch (refreshError) {
      Sentry.addBreadcrumb({
        category: 'auth',
        level: 'warning',
        message: 'Refresh after 401 failed',
        data: { endpoint, method, error_code: refreshError?.code },
      });
    }
    if (refreshed) {
      return fetchApiResponse(
        relativeUrl,
        options,
        requestHeaders,
        impersonate,
        { ...retried, afterRefresh: true }
      );
    }
  }

  if (response.status < 200 || response.status >= 300) {
    const errorMessage = response.statusText
      ? response.statusText
      : 'Heroku Fetch Error';

    const error = new ApiError(relativeUrl, errorMessage);
    error.response = response;
    error.status = response.status;
    error.method = method;
    error.endpoint = endpoint;
    error.durationMs = durationMs;
    throw error;
  }

  return response;
};

export const apiFetch = async (
  relativeUrl,
  options,
  headers = {},
  impersonate = true
) => {
  const response = await fetchApiResponse(
    relativeUrl,
    options,
    headers,
    impersonate
  );

  const { status, headers: responseHeaders } = response;
  // If response code is 204, then return null, if not, try to parse json
  // 204 No Content means that the response is successfull, but there is no
  // response body, and thusly, it would cause an error trying to parse it.
  const responseBody = status === 204 ? null : await response.json();

  return {
    body: responseBody,
    headers: responseHeaders,
    status,
  };
};

export const apiFetchRaw = async (
  relativeUrl,
  options,
  headers = {},
  impersonate = true
) => {
  const response = await fetchApiResponse(
    relativeUrl,
    options,
    headers,
    impersonate
  );

  return {
    body: response.body,
    headers: response.headers,
    response,
    status: response.status,
  };
};

export const apiImpersonate = (ssid) => {
  impersonateId = ssid;
};
